// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI XR Studio — embed code for a delivered video (plan J11, the part that
 * keeps outlets hosting their own files).
 *
 * The outlet puts the file and its poster on its own site and pastes this
 * code into an article: a responsive player that loads nothing until a
 * reader presses play (`preload="none"`, the poster shows meanwhile), or a
 * lazy image for a GIF or a still. Sizes are set so the page does not jump
 * while it loads. AYNI hosts nothing for readers, so no reader reaches our
 * servers and the plan's DSA position holds (docs/AYNI_XR_JOURNALISM_PLAN.md);
 * hosted embeds with view counts wait on the owner.
 */

import { FORMAT_FILE, outputSize, type ShotOutput } from './shot'

/** What the outlet replaces with its own address. */
export const EMBED_SITE = 'https://YOUR-SITE'

/** Formats an article can show; ProRes is for editing suites. */
export const embeddable = (output: Pick<ShotOutput, 'format'>): boolean => output.format !== 'prores'

/** The GIF's pixels: the worker scales it to 540 on the short side. */
function shownSize(output: ShotOutput): { width: number; height: number } {
  const { width, height } = outputSize(output.aspect, output.resolution)
  if (output.format !== 'gif') return { width, height }
  const scale = 540 / Math.min(width, height)
  return { width: Math.round(width * scale), height: Math.round(height * scale) }
}

/** The embed code for job `jobId`, or null for a format no article shows. */
export function embedSnippet(jobId: string, output: ShotOutput): string | null {
  if (!embeddable(output)) return null
  const file = `${EMBED_SITE}/ayni-studio-${jobId}.${FORMAT_FILE[output.format].ext}`
  const { width, height } = shownSize(output)
  if (output.format === 'gif' || output.format === 'png') {
    return `<img src="${file}" alt="(describe what it shows)" width="${width}" height="${height}" loading="lazy" decoding="async" style="display:block;width:100%;height:auto">`
  }
  const type = output.format === 'hevc' ? 'video/mp4; codecs=&quot;hvc1&quot;' : 'video/mp4'
  return [
    `<video controls playsinline preload="none" width="${width}" height="${height}"`,
    `  poster="${EMBED_SITE}/ayni-studio-${jobId}-poster.jpg"`,
    `  style="display:block;width:100%;height:auto;background:#000">`,
    `  <source src="${file}" type="${type}">`,
    `</video>`,
  ].join('\n')
}
